import { useNavigate } from "react-router-dom";
import { Crown, Eye, LogOut, Shield, User as UserIcon, X } from "lucide-react";
import { useImpersonation, type ImpersonationRole } from "@/contexts/ImpersonationContext";
import { Button } from "@/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

const ROLE_OPTIONS: Array<{
  value: Exclude<ImpersonationRole, null>;
  label: string;
  short: string;
  icon: typeof UserIcon;
}> = [
  { value: "anonymous",    label: "Ziyaretçi",    short: "Ziyaretçi",   icon: Eye },
  { value: "guest_member", label: "Misafir Üye",  short: "Misafir",     icon: UserIcon },
  { value: "premium",      label: "Strength in Range Üyesi", short: "Strength", icon: Crown },
];

const ImpersonationBar = () => {
  const { realIsAdmin, role, status, endsInDays, setRole, isImpersonating } = useImpersonation();
  const navigate = useNavigate();

  if (!realIsAdmin || !isImpersonating) return null;

  const showStatus = role !== "anonymous";
  const statusLabel = status === "canceled" ? "İptal" : status === "expired" ? "Bitti" : "Aktif";
  const statusTone =
    status === "expired"
      ? "bg-destructive/15 text-destructive border-destructive/30"
      : status === "canceled"
      ? "bg-amber-500/15 text-amber-500 border-amber-500/30"
      : "bg-emerald-500/10 text-emerald-500 border-emerald-500/30";
  const endsLabel = endsInDays === 0
    ? "bugün"
    : endsInDays > 0
    ? `${endsInDays}g sonra`
    : `${Math.abs(endsInDays)}g önce`;


  return (
    <AnimatePresence>
      <motion.div
        key="impersonation-bar"
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 80, opacity: 0 }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
        className="fixed z-[60] w-auto bottom-[5rem] md:bottom-8 right-[5rem] md:right-[7.5rem] max-w-[calc(100vw-6rem)] md:max-w-[calc(100vw-8.5rem)]"
      >
        <div className="h-14 rounded-full border border-primary/40 bg-background/95 backdrop-blur-md shadow-lg shadow-primary/20 px-3 sm:px-4 flex items-center gap-1.5 sm:gap-2 max-w-full overflow-hidden">
          <div className="flex items-center gap-1.5 pl-1 sm:px-2 shrink-0">
            <Shield size={14} className="text-primary" />
            <span className="text-[10px] sm:text-xs font-semibold tracking-wider uppercase text-primary hidden sm:inline">
              Test
            </span>
          </div>

          <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide min-w-0">
            {ROLE_OPTIONS.map((opt) => {
              const isActive = opt.value === role;
              return (
                <button
                  key={opt.value}
                  onClick={() => setRole(opt.value)}
                  className={`shrink-0 inline-flex items-center gap-1 px-1.5 sm:px-2 py-1 rounded-full text-[10px] sm:text-xs font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                  }`}
                  aria-pressed={isActive}
                  aria-label={opt.label}
                  title={opt.label}
                >
                  <opt.icon size={13} />
                  <span className="hidden sm:inline">{opt.short}</span>
                </button>
              );
            })}
          </div>

          {showStatus && (
            <span
              className={`shrink-0 hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold ${statusTone}`}
              title={`Abonelik ${statusLabel.toLowerCase()} · bitiş ${endsLabel}`}
            >
              {statusLabel} · {endsLabel}
            </span>
          )}

          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[10px] sm:text-xs shrink-0"
            onClick={() => { setRole(null); navigate("/admin"); }}
            title="Test modundan çık"
          >
            <LogOut size={12} />
            <span className="hidden sm:inline">Çıkış</span>
            <X size={12} className="sm:hidden" />
          </Button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ImpersonationBar;
